import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const services = [
  { id: 'checkup', name: 'General Checkup', duration: '30 min', icon: '🦷' },
  { id: 'cleaning', name: 'Teeth Cleaning', duration: '45 min', icon: '✨' },
  { id: 'whitening', name: 'Teeth Whitening', duration: '60 min', icon: '💎' },
  { id: 'veneers', name: 'Veneer Consultation', duration: '45 min', icon: '🌟' },
  { id: 'invisalign', name: 'Invisalign Consultation', duration: '60 min', icon: '😁' },
  { id: 'emergency', name: 'Emergency Visit', duration: '30 min', icon: '🚨' },
];

const timeSlots = [
  '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM',
];

// Simulated availability — some slots "booked"
function generateAvailability(date: Date): Map<string, 'available' | 'limited' | 'booked'> {
  const map = new Map<string, 'available' | 'limited' | 'booked'>();
  const seed = date.getDate() + date.getMonth();
  timeSlots.forEach((slot, i) => {
    const hash = (seed * 17 + i * 31) % 10;
    if (hash < 4) map.set(slot, 'available');
    else if (hash < 7) map.set(slot, 'limited');
    else map.set(slot, 'booked');
  });
  return map;
}

function getWeekDates(base: Date): Date[] {
  const start = new Date(base);
  start.setDate(start.getDate() - start.getDay() + 1); // Monday
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function AppointmentScheduler() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState(services[0].id);
  const [step, setStep] = useState(1); // 1: service, 2: date/time, 3: confirm
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);

  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);
  const availability = useMemo(() => generateAvailability(selectedDate), [selectedDate]);

  const nextWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 7);
    setSelectedDate(d);
  };
  const prevWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 7);
    setSelectedDate(d);
  };

  const selectedServiceData = services.find(s => s.id === selectedService);

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <section id="appointment" className="section-padding relative">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-3xl p-12 shadow-2xl shadow-[var(--color-primary)]/10"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)] flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--font-display)' }}>Appointment Confirmed!</h3>
            <p className="text-[var(--color-text-light)] text-lg mb-2">
              Thank you, <span className="font-semibold text-[var(--color-navy)]">{formData.name}</span>!
            </p>
            <p className="text-[var(--color-text-light)] mb-6">
              Your <span className="font-semibold text-[var(--color-primary)]">{selectedServiceData?.name}</span> appointment is booked for{' '}
              <span className="font-semibold text-[var(--color-navy)]">
                {dayNames[weekDates.indexOf(selectedDate)]}, {monthNames[selectedDate.getMonth()]} {selectedDate.getDate()} at {selectedTime}
              </span>.
            </p>
            <p className="text-sm text-[var(--color-text-light)] mb-8">
              A confirmation email has been sent to <span className="font-semibold">{formData.email}</span>.
              Please arrive 10 minutes before your appointment.
            </p>
            <button
              onClick={() => { setSubmitted(false); setStep(1); setSelectedTime(null); }}
              className="px-8 py-3 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            >
              Book Another Appointment
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="appointment" className="section-padding relative">
      <div className="absolute inset-0 dot-pattern opacity-20" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-accent)]/10 text-[var(--color-accent)] text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Book Your Appointment
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'var(--font-display)' }}>
            Schedule Your <span className="gradient-text">Visit</span>
          </h2>
          <p className="text-lg text-[var(--color-text-light)] leading-relaxed">
            Real-time availability — pick your preferred service, date, and time. We'll confirm instantly.
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-10 max-w-md mx-auto">
          {['Service', 'Date & Time', 'Your Info'].map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-300 ${
                step > i + 1 ? 'bg-[var(--color-primary)] text-white' :
                step === i + 1 ? 'bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)] text-white shadow-lg shadow-[var(--color-primary)]/30' :
                'bg-gray-100 text-gray-400'
              }`}>
                {step > i + 1 ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${step === i + 1 ? 'text-[var(--color-primary)]' : 'text-gray-400'}`}>{label}</span>
              {i < 2 && <div className={`flex-1 h-0.5 rounded-full ${step > i + 1 ? 'bg-[var(--color-primary)]' : 'bg-gray-100'}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Service Selection */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4 }}
              className="max-w-3xl mx-auto"
            >
              <h3 className="text-xl font-semibold text-center mb-8" style={{ fontFamily: 'var(--font-display)' }}>Select a Service</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`p-5 rounded-2xl text-left transition-all duration-300 ${
                      selectedService === service.id
                        ? 'bg-white shadow-xl shadow-[var(--color-primary)]/10 border-2 border-[var(--color-primary)]/30 scale-[1.02]'
                        : 'bg-white/60 hover:bg-white border-2 border-transparent hover:border-[var(--color-primary)]/10'
                    }`}
                  >
                    <div className="text-2xl mb-2">{service.icon}</div>
                    <h4 className="font-semibold text-[var(--color-navy)]">{service.name}</h4>
                    <p className="text-sm text-[var(--color-text-light)]">{service.duration}</p>
                  </button>
                ))}
              </div>
              <div className="text-center">
                <button
                  onClick={() => setStep(2)}
                  className="px-8 py-3 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                >
                  Choose Date & Time →
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4 }}
              className="max-w-4xl mx-auto"
            >
              {/* Week navigation */}
              <div className="bg-white rounded-3xl shadow-xl shadow-black/5 p-6 lg:p-8 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <button onClick={prevWeek} className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-colors">
                    <svg className="w-5 h-5 text-[var(--color-text)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h3 className="text-lg font-semibold text-[var(--color-navy)]" style={{ fontFamily: 'var(--font-display)' }}>
                    {monthNames[weekDates[0].getMonth()]} – {monthNames[weekDates[4].getMonth()]} {weekDates[4].getFullYear()}
                  </h3>
                  <button onClick={nextWeek} className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-colors">
                    <svg className="w-5 h-5 text-[var(--color-text)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-5 gap-3">
                  {weekDates.map((date, i) => {
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isSelected = date.toDateString() === selectedDate.toDateString();
                    return (
                      <button
                        key={i}
                        onClick={() => { setSelectedDate(date); setSelectedTime(null); }}
                        className={`relative p-4 rounded-2xl text-center transition-all duration-300 ${
                          isSelected
                            ? 'bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)] text-white shadow-lg shadow-[var(--color-primary)]/25'
                            : 'bg-gray-50 hover:bg-gray-100 text-[var(--color-text)]'
                        }`}
                      >
                        <p className={`text-xs font-medium mb-1 ${isSelected ? 'text-white/70' : 'text-[var(--color-text-light)]'}`}>{dayNames[i]}</p>
                        <p className="text-2xl font-bold">{date.getDate()}</p>
                        {isToday && (
                          <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${isSelected ? 'bg-white' : 'bg-[var(--color-accent)]'}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time slots */}
              <div className="bg-white rounded-3xl shadow-xl shadow-black/5 p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-[var(--color-navy)]" style={{ fontFamily: 'var(--font-display)' }}>
                    Available Times
                  </h3>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-400" /> Available</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400" /> Limited</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-300" /> Booked</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  {timeSlots.map((slot) => {
                    const status = availability.get(slot);
                    const isSelected = selectedTime === slot;
                    return (
                      <button
                        key={slot}
                        disabled={status === 'booked'}
                        onClick={() => setSelectedTime(slot)}
                        className={`relative py-3 px-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                          isSelected
                            ? 'bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)] text-white shadow-lg shadow-[var(--color-primary)]/25 scale-105'
                            : status === 'booked'
                            ? 'bg-gray-50 text-gray-300 cursor-not-allowed line-through'
                            : status === 'limited'
                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 hover:scale-105'
                        }`}
                      >
                        {slot}
                        {status === 'limited' && !isSelected && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button onClick={() => setStep(1)} className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-[var(--color-text)] font-medium rounded-xl transition-colors">
                  ← Back
                </button>
                <button
                  onClick={() => selectedTime && setStep(3)}
                  disabled={!selectedTime}
                  className={`px-8 py-3 font-semibold rounded-xl shadow-lg transition-all ${
                    selectedTime
                      ? 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] text-white shadow-[var(--color-primary)]/25 hover:shadow-[var(--color-primary)]/40 hover:scale-105'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                  }`}
                >
                  Continue →
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-white rounded-3xl shadow-2xl shadow-black/5 p-8 lg:p-10">
                {/* Summary */}
                <div className="bg-gradient-to-br from-[var(--color-primary)]/5 to-[var(--color-accent)]/5 rounded-2xl p-6 mb-8">
                  <h3 className="text-lg font-semibold text-[var(--color-navy)] mb-4" style={{ fontFamily: 'var(--font-display)' }}>Appointment Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-light)]">Service</span>
                      <span className="font-medium text-[var(--color-navy)]">{selectedServiceData?.icon} {selectedServiceData?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-light)]">Date</span>
                      <span className="font-medium text-[var(--color-navy)]">
                        {dayNames[weekDates.indexOf(selectedDate)]}, {monthNames[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-light)]">Time</span>
                      <span className="font-medium text-[var(--color-navy)]">{selectedTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-light)]">Duration</span>
                      <span className="font-medium text-[var(--color-navy)]">{selectedServiceData?.duration}</span>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/10 outline-none transition-all text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/10 outline-none transition-all text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/10 outline-none transition-all text-sm"
                        placeholder="(201) 985-8877"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">Additional Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/10 outline-none transition-all text-sm resize-none"
                      rows={3}
                      placeholder="Any special requests or concerns..."
                    />
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <button onClick={() => setStep(2)} className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-[var(--color-text)] font-medium rounded-xl transition-colors">
                    ← Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!formData.name || !formData.email}
                    className={`px-8 py-3 font-semibold rounded-xl shadow-lg transition-all ${
                      formData.name && formData.email
                        ? 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] text-white shadow-[var(--color-primary)]/25 hover:shadow-[var(--color-primary)]/40 hover:scale-105'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                    }`}
                  >
                    Confirm Booking ✓
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
